// src/components/SurveyQuestion.jsx
// Reusable Survey Question Component
// Handles radio, checkbox, slider, ranking (+ safe fallbacks for text/textarea)

import React, { useState, useEffect, useRef } from "react";

const AP_BLUE = "#18365E";
const AP_ORANGE = "#FF6E4C";
const GRAY_200 = "#E6ECF2";
const GRAY_300 = "#d1d5db";

function normalizeType(t) {
  const v = (t || "").toLowerCase().trim();
  if (["radio", "single", "single-choice", "single_choice", "single_select"].includes(v)) return "radio";
  if (["checkbox", "multi", "multi-choice", "multiple-choice", "multi_select"].includes(v)) return "checkbox";
  if (["slider", "scale"].includes(v)) return "slider";
  if (["ranking", "rank"].includes(v)) return "ranking";
  if (["long_text", "textarea", "paragraph"].includes(v)) return "textarea";
  if (["text", "short_text", "input"].includes(v)) return "text";
  return v || "text";
}

function normalizeOptions(options) {
  if (!options) return [];
  if (Array.isArray(options)) {
    return options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  }
  try {
    const arr = JSON.parse(options);
    return Array.isArray(arr)
      ? arr.map((o) => (typeof o === "string" ? { value: o, label: o } : o))
      : [];
  } catch {
    return [];
  }
}

const SurveyQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  onNext,
  error,
  disabled = false,
  autoFocus = false,
  renderTitle = true, // when false, caller shows prompt elsewhere
  density = "regular", // 'regular' | 'compact'
  columns = 1, // 1 | 2 | 3 (radio/checkbox only)
}) => {
  const type = normalizeType(question?.response_type);
  const opts = normalizeOptions(question?.options);

  const [localValue, setLocalValue] = useState(value ?? (type === "checkbox" ? [] : ""));
  const [rankingOrder, setRankingOrder] = useState(Array.isArray(value) ? value : []);
  const [isAnimating, setIsAnimating] = useState(false);
  const questionRef = useRef(null);
  const firstOptionRef = useRef(null);

  // Sync outside changes into local state
  useEffect(() => {
    if (type === "checkbox") {
      setLocalValue(Array.isArray(value) ? value : []);
    } else {
      setLocalValue(value ?? "");
    }
    if (type === "ranking") {
      setRankingOrder(Array.isArray(value) ? value : []);
    }
     
  }, [value, type]);

  // Smooth autofocus + center scroll on activation
  useEffect(() => {
    if (autoFocus && questionRef.current) {
      setIsAnimating(true);
      setTimeout(() => {
        questionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        if (firstOptionRef.current) firstOptionRef.current.focus();
        setIsAnimating(false);
      }, 100);
    }
  }, [autoFocus]);

  // Keyboard navigation for radio + quick-select 1..9
  const handleKeyDown = (e) => {
    if (disabled) return;

    if (type === "radio") {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        handleArrowNavigation(e.key === "ArrowDown");
        return;
      }
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= 9 && opts[n - 1]) {
        e.preventDefault();
        handleChange(opts[n - 1].value ?? opts[n - 1].label);
        return;
      }
    }
    if (e.key === "Enter" && onNext) {
      // advance on Enter if there is a value
      if ((type === "checkbox" && Array.isArray(localValue) && localValue.length) || (type !== "checkbox" && localValue)) {
        e.preventDefault();
        onNext();
      }
    }
  };

  const handleArrowNavigation = (isDown) => {
    const list = opts.map((o) => o.value ?? o.label);
    const currentIndex = list.indexOf(localValue);
    const nextIndex = isDown
      ? (currentIndex + 1) % list.length
      : (currentIndex - 1 + list.length) % list.length;
    handleChange(list[nextIndex]);
  };

  const handleChange = (newValue) => {
    setLocalValue(newValue);
    onChange?.(newValue);
    if (type === "radio" && onNext) {
      // small delay to let user see the selection
      setTimeout(() => onNext(), 600);
    }
  };

  const handleBlur = () => onBlur?.(localValue);

  const handleCheckboxChange = (option, checked) => {
    const cur = Array.isArray(localValue) ? localValue : [];
    const v = option.value ?? option.label;
    const next = checked ? [...cur, v] : cur.filter((x) => x !== v);
    handleChange(next);
  };

  const handleRankingChange = (draggedItem, targetIndex) => {
    const newOrder = [...rankingOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    if (draggedIndex !== -1) newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    setRankingOrder(newOrder);
    handleChange(newOrder);
  };

  // Shared UI tokens
  const isCompact = density === "compact";
  const minRowH = isCompact ? 36 : 44;
  const gap = isCompact ? 12 : 16;

  // RADIO
  const renderRadioQuestion = () => {
    const containerStyle = {
      display: columns > 1 ? "grid" : "flex",
      flexDirection: columns > 1 ? "row" : "column",
      gridTemplateColumns: columns > 1 ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
      gap,
      flexWrap: "wrap",
    };

    return (
      <div style={containerStyle} onKeyDown={handleKeyDown}>
        {opts.map((option, index) => {
          const val = option.value ?? option.label;
          const isSelected = localValue === val;

          return (
            <label
              key={val}
              ref={index === 0 ? firstOptionRef : null}
              tabIndex={disabled ? -1 : 0}
              style={{
                display: "flex",
                alignItems: "center",
                padding: isCompact ? "10px 14px" : "20px 24px",
                borderRadius: 12,
                border: isSelected ? `2px solid ${AP_ORANGE}` : `1px solid ${GRAY_200}`,
                backgroundColor: isSelected ? "#fff7ed" : "#fff",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "all 0.2s ease-in-out",
                opacity: disabled ? 0.6 : 1,
                boxShadow: isSelected
                  ? "0 4px 12px rgba(255,110,76,0.2)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
                outline: "none",
                minHeight: minRowH,
                flex: columns > 1 ? "1 1 40%" : "1 1 100%",
              }}
              onMouseEnter={(e) => {
                if (!disabled && !isSelected) {
                  const t = e.currentTarget;
                  t.style.borderColor = "#FF8A6B";
                  t.style.backgroundColor = "#FEFAF6";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) {
                  const t = e.currentTarget;
                  t.style.borderColor = GRAY_200;
                  t.style.backgroundColor = "#fff";
                }
              }}
              onFocus={(e) => {
                if (!disabled) e.currentTarget.style.boxShadow = "0 0 0 3px rgba(24,54,94,0.2)";
              }}
              onBlur={(e) => {
                if (!disabled) {
                  e.currentTarget.style.boxShadow = isSelected
                    ? "0 4px 12px rgba(255,110,76,0.2)"
                    : "0 1px 2px rgba(0,0,0,0.05)";
                }
              }}
              onClick={() => !disabled && handleChange(val)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  !disabled && handleChange(val);
                }
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  border: `2px solid ${isSelected ? AP_ORANGE : GRAY_300}`,
                  backgroundColor: isSelected ? AP_ORANGE : "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 16,
                  transition: "all 0.3s ease",
                }}
              >
                {isSelected && <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff" }} />}
              </div>

              <input
                type="radio"
                name={question.id}
                value={val}
                checked={isSelected}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                disabled={disabled}
                style={{ display: "none" }}
              />

              <span
                style={{
                  fontSize: 16,
                  color: isSelected ? AP_BLUE : "#374151",
                  fontFamily: "Montserrat, system-ui, sans-serif",
                  fontWeight: isSelected ? 600 : 500,
                  lineHeight: 1.5,
                }}
              >
                {option.label ?? option.value}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  // CHECKBOX
  const renderCheckboxQuestion = () => {
    const containerStyle = {
      display: columns > 1 ? "grid" : "flex",
      flexDirection: columns > 1 ? undefined : "column",
      gridTemplateColumns: columns > 1 ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
      gap,
    };
    const cur = Array.isArray(localValue) ? localValue : [];

    return (
      <div style={containerStyle}>
        {opts.map((option) => {
          const val = option.value ?? option.label;
          const isChecked = cur.includes(val);

          return (
            <label
              key={val}
              style={{
                display: "flex",
                alignItems: "center",
                padding: isCompact ? "12px 16px" : "20px 24px",
                borderRadius: 12,
                border: isChecked ? `2px solid ${AP_ORANGE}` : "1px solid #e5e7eb",
                backgroundColor: isChecked ? "#fff7ed" : "#fff",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                opacity: disabled ? 0.5 : 1,
                boxShadow: isChecked ? "0 4px 12px rgba(255,110,76,0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                minHeight: minRowH,
              }}
              onMouseEnter={(e) => {
                if (!disabled && !isChecked) {
                  const t = e.currentTarget;
                  t.style.borderColor = "#FF8A6B";
                  t.style.backgroundColor = "#FEF7F0";
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isChecked) {
                  const t = e.currentTarget;
                  t.style.borderColor = "#e5e7eb";
                  t.style.backgroundColor = "#fff";
                }
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: `2px solid ${isChecked ? AP_ORANGE : GRAY_300}`,
                  backgroundColor: isChecked ? AP_ORANGE : "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 16,
                  transition: "all 0.3s ease",
                }}
              >
                {isChecked && (
                  <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                    <path d="M1 4.5L4.5 8L11 1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>

              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                onBlur={handleBlur}
                disabled={disabled}
                style={{ display: "none" }}
              />

              <span
                style={{
                  fontSize: 16,
                  color: isChecked ? AP_BLUE : "#374151",
                  fontFamily: "Montserrat, system-ui, sans-serif",
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: 1.5,
                }}
              >
                {option.label ?? option.value}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  // SLIDER
  const renderSliderQuestion = () => {
    const currentValue =
      typeof localValue === "number" ? localValue : question.min ?? 0;
    const pct =
      question.max && question.max !== question.min
        ? ((currentValue - question.min) / (question.max - question.min)) * 100
        : 0;

    return (
      <div style={{ padding: "24px 0" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              display: "inline-block",
              backgroundColor: AP_ORANGE,
              color: "#fff",
              padding: "10px 20px",
              borderRadius: 24,
              fontSize: 22,
              fontWeight: 700,
              fontFamily: "Montserrat, system-ui, sans-serif",
              boxShadow: "0 4px 12px rgba(255,110,76,0.3)",
            }}
          >
            {currentValue}
          </div>
        </div>

        <div style={{ position: "relative", padding: "0 24px", marginBottom: 16 }}>
          <div
            style={{
              height: 8,
              backgroundColor: "#e5e7eb",
              borderRadius: 4,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 4,
                width: `${pct}%`,
                transition: "width 0.3s ease",
                background: "linear-gradient(90deg, #FF6E4C 0%, #FF8A6B 100%)",
              }}
            />
          </div>

          <input
            type="range"
            min={question.min}
            max={question.max}
            step={question.step || 1}
            value={currentValue}
            onChange={(e) => handleChange(parseInt(e.target.value, 10))}
            onBlur={handleBlur}
            disabled={disabled}
            style={{
              position: "absolute",
              top: -8,
              left: 24,
              right: 24,
              width: "calc(100% - 48px)",
              height: 24,
              background: "transparent",
              outline: "none",
              appearance: "none",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 24px",
            fontSize: 14,
            fontFamily: "Montserrat, system-ui, sans-serif",
          }}
        >
          <div style={{ textAlign: "left", color: "#6b7280", fontWeight: 500 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>MIN</div>
            <div style={{ color: AP_BLUE, fontWeight: 600 }}>
              {question.labels?.[question.min] ?? question.min}
            </div>
          </div>
          <div style={{ textAlign: "right", color: "#6b7280", fontWeight: 500 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>MAX</div>
            <div style={{ color: AP_BLUE, fontWeight: 600 }}>
              {question.labels?.[question.max] ?? question.max}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // RANKING
  const renderRankingQuestion = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p
        style={{
          fontSize: 14,
          color: "#6b7280",
          marginBottom: 12,
          fontFamily: "Montserrat, system-ui, sans-serif",
        }}
      >
        Drag and drop to rank in order of priority (1 = highest priority):
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {opts.map((option, index) => {
          const val = option.value ?? option.label;
          const currentRank = rankingOrder.indexOf(val) + 1;
          const isRanked = rankingOrder.includes(val);

          return (
            <div
              key={val}
              draggable={!disabled}
              onDragStart={(e) => e.dataTransfer.setData("text/plain", val)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const draggedItem = e.dataTransfer.getData("text/plain");
                handleRankingChange(draggedItem, index);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: 12,
                borderRadius: 8,
                border: isRanked ? `1px solid ${AP_ORANGE}` : "1px solid #e5e7eb",
                backgroundColor: isRanked ? "#fff7ed" : "#fff",
                cursor: disabled ? "not-allowed" : "move",
                transition: "all 0.2s ease",
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  color: "#374151",
                  fontFamily: "Montserrat, system-ui, sans-serif",
                }}
              >
                {option.label ?? option.value}
              </span>
              {isRanked && (
                <span
                  style={{
                    backgroundColor: AP_ORANGE,
                    color: "#fff",
                    fontSize: 12,
                    padding: "4px 8px",
                    borderRadius: 12,
                    fontWeight: 600,
                    fontFamily: "Montserrat, system-ui, sans-serif",
                  }}
                >
                  #{currentRank}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Simple fallbacks for unexpected types
  const renderText = () => (
    <input
      type="text"
      value={localValue ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={question.placeholder || ""}
      style={{
        width: "100%",
        borderRadius: 10,
        border: `1px solid ${GRAY_200}`,
        padding: isCompact ? "10px 12px" : "12px 14px",
        fontSize: 15,
        outline: "none",
      }}
    />
  );

  const renderTextarea = () => (
    <textarea
      rows={isCompact ? 4 : 6}
      value={localValue ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={question.placeholder || ""}
      style={{
        width: "100%",
        borderRadius: 10,
        border: `1px solid ${GRAY_200}`,
        padding: isCompact ? "10px 12px" : "12px 14px",
        fontSize: 15,
        outline: "none",
        resize: "vertical",
      }}
    />
  );

  // Body chooser
  let body = null;
  switch (type) {
    case "radio":
      body = renderRadioQuestion();
      break;
    case "checkbox":
      body = renderCheckboxQuestion();
      break;
    case "slider":
      body = renderSliderQuestion();
      break;
    case "ranking":
      body = renderRankingQuestion();
      break;
    case "textarea":
      body = renderTextarea();
      break;
    case "text":
    default:
      body = renderText();
      break;
  }

  // Wrapper: when renderTitle=false, return body only (for compact page layout)
  if (!renderTitle) {
    return (
      <div ref={questionRef} onKeyDown={handleKeyDown}>
        {body}
      </div>
    );
  }

  // Legacy / card mode (kept for compatibility)
  return (
    <div
      ref={questionRef}
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        padding: 32,
        marginBottom: 24,
        boxShadow: autoFocus
          ? "0 8px 32px rgba(255,110,76,0.15), 0 0 0 2px rgba(255,110,76,0.1)"
          : "0 4px 16px rgba(0,0,0,0.08)",
        border: autoFocus ? `2px solid ${AP_ORANGE}` : "2px solid #f1f5f9",
        transition: "all 0.5s ease",
        transform: isAnimating ? "scale(1.02)" : "scale(1)",
        opacity: isAnimating ? 0.9 : 1,
      }}
      onKeyDown={handleKeyDown}
    >
      <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `2px solid ${AP_BLUE}` }}>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: AP_BLUE,
            margin: "0 0 8px 0",
            fontFamily: "Montserrat, system-ui, sans-serif",
            lineHeight: 1.4,
          }}
        >
          {question?.question_text}
          {question?.required && (
            <span style={{ color: AP_ORANGE, marginLeft: 8, fontSize: 18 }}>*</span>
          )}
        </h3>

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: "12px 16px",
              marginTop: 12,
            }}
          >
            <p
              style={{
                color: "#dc2626",
                fontSize: 14,
                margin: 0,
                fontFamily: "Montserrat, system-ui, sans-serif",
                fontWeight: 500,
              }}
            >
              ⚠️ {error}
            </p>
          </div>
        )}
      </div>

      <div>{body}</div>
    </div>
  );
};

export default SurveyQuestion;
